import axios, {
    AxiosInstance,
    AxiosResponse,
    AxiosError,
    InternalAxiosRequestConfig,
} from 'axios';
import { getAuthToken, clearAuthToken } from '@/utils/auth';
import { clearImpersonationRedirectHint } from '@/utils/impersonationHint';
import { clearAuth } from '@/store/slices/authSlice';
import { queryClient } from '@/lib/queryClient';

/**
 * API Base URL configuration
 */
const baseURL: string =
    import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';

/**
 * Create an Axios instance with predefined settings.
 *
 * withCredentials: true sends httpOnly cookies automatically (future).
 * Bearer token interceptor is still active for backward compat with current backend.
 */
const apiClient: AxiosInstance = axios.create({
    baseURL,
    timeout: 30000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Lazy store import to avoid circular dependencies
let _store: any = null;
const getStore = () => {
    if (!_store) {
        _store = require('@/store').store;
    }
    return _store;
};

/**
 * Request Interceptor
 * Adds Authorization token to each request if available.
 * This is the EXISTING mechanism — it will be removed once the backend
 * fully switches to httpOnly cookies.
 */
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig<any>) => {
        const token: string | undefined = getAuthToken();
        if (token) {
            config.headers.set('Authorization', `Bearer ${token}`);
        }
        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

/**
 * Flag to prevent multiple 401 redirects firing simultaneously
 */
let isRedirecting = false;

/**
 * Feature blocked state — set when feature_not_enabled is detected.
 * Exported so hooks can read the initial state on mount (events may fire
 * before listeners are attached).
 */
export let featureBlockedState = { blocked: false, message: '' };

/**
 * Reset the feature blocked state. Must be called on logout to prevent
 * the blocked flag from leaking across accounts.
 */
export const resetFeatureBlockedState = () => {
    featureBlockedState = { blocked: false, message: '' };
};

/**
 * Response Interceptor
 * Handles 401 errors globally: clears auth state, clears cache, redirects to login.
 */
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async (error: AxiosError) => {
        if (error.response) {
            // Handle 401 Unauthorized — session expired or invalid
            if (error.response.status === 401 && !isRedirecting && !featureBlockedState.blocked) {
                const requestUrl = error.config?.url || '';
                if (!requestUrl.includes('/auth/me') && !requestUrl.includes('/auth/login')) {
                    isRedirecting = true;
                    try {
                        clearAuthToken();
                        clearImpersonationRedirectHint();
                        resetFeatureBlockedState();
                        const store = getStore();
                        if (store) {
                            store.dispatch(clearAuth());
                        }
                        queryClient.clear();
                        window.location.href = '/login';
                    } finally {
                        setTimeout(() => {
                            isRedirecting = false;
                        }, 2000);
                    }
                }
            }

            // Handle 403 Forbidden — team plan blocked, store access denied, OR membership revoked
            if (error.response.status === 403) {
                const errData = error.response.data as any;

                // Feature not enabled — non-owner blocked due to feature gating
                if (errData?.error === 'feature_not_enabled') {
                    featureBlockedState = {
                        blocked: true,
                        message: errData.message || 'This feature is not enabled for your organization. Contact support.',
                    };
                    return Promise.reject(error);
                }

                // If feature is already blocked, skip all other 403 handling.
                // Other APIs may return generic 403s — don't let those trigger
                // membership-revoked redirects.
                if (featureBlockedState.blocked) {
                    return Promise.reject(error);
                }

                const errorMsg =
                    errData?.error ||
                    errData?.message ||
                    errData?.error?.message ||
                    '';

                if (typeof errorMsg === 'string') {
                    const lowerMsg = errorMsg.toLowerCase();

                    if (lowerMsg.includes('do not have access to this store')) {
                        // Store-level access denied — show toast
                        window.dispatchEvent(
                            new CustomEvent('store-access-denied', {
                                detail: { message: errorMsg },
                            })
                        );
                    } else if (
                        lowerMsg.includes('removed') ||
                        lowerMsg.includes('revoked') ||
                        lowerMsg.includes('membership') ||
                        lowerMsg.includes('not a member') ||
                        lowerMsg.includes('access denied') ||
                        lowerMsg.includes('no longer')
                    ) {
                        // Membership revoked — clear session and redirect to login
                        if (!isRedirecting) {
                            isRedirecting = true;
                            try {
                                clearAuthToken();
                                clearImpersonationRedirectHint();
                                resetFeatureBlockedState();
                                const store = getStore();
                                if (store) {
                                    store.dispatch(clearAuth());
                                }
                                queryClient.clear();
                                window.location.href = '/login?reason=access_revoked';
                            } finally {
                                setTimeout(() => {
                                    isRedirecting = false;
                                }, 2000);
                            }
                        }
                    }
                }
            }

            if (process.env.NODE_ENV === 'development') {
                console.error('API Error:', error.response.status, error.response.data);
            }
        } else if (error.request) {
            if (process.env.NODE_ENV === 'development') {
                console.error('No response received:', error.message);
            }
        } else {
            if (process.env.NODE_ENV === 'development') {
                console.error('Request setup error:', error.message);
            }
        }

        return Promise.reject(error);
    }
);

/**
 * Exports the configured Axios instance.
 */
export default apiClient;
